import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Check } from "lucide-react";
import NorthStar from "../components/NorthStar";
import client, { formatApiError } from "../lib/api";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const APP_FIELDS = [
  ["email", "Email"], ["phone", "Phone"], ["primary_social", "Social"],
  ["platform_links", "Links"], ["audience_size", "Audience"], ["content_category", "Category"],
  ["management_status", "Management"], ["why_interested", "Why NSTR"], ["anything_else", "Other"],
];
const BRAND_FIELDS = [
  ["work_email", "Email"], ["company", "Company"], ["campaign", "Campaign"],
  ["talent_requested", "Talent"], ["budget_range", "Budget"], ["timeline", "Timeline"],
  ["additional_details", "Details"],
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("applications");
  const [apps, setApps] = useState([]);
  const [brands, setBrands] = useState([]);
  const [stats, setStats] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [a, b, s] = await Promise.all([
        client.get("/admin/applications"),
        client.get("/admin/brand-inquiries"),
        client.get("/admin/stats"),
      ]);
      setApps(a.data);
      setBrands(b.data);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/admin/login");
      } else {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("nstr_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const logout = () => {
    localStorage.removeItem("nstr_token");
    navigate("/admin/login");
  };

  const markReviewed = async (type, id) => {
    const path = type === "applications" ? "applications" : "brand-inquiries";
    try {
      await client.patch(`/admin/${path}/${id}`, { status: "reviewed" });
      toast.success("Marked as reviewed.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const items = tab === "applications" ? apps : brands;
  const fields = tab === "applications" ? APP_FIELDS : BRAND_FIELDS;

  return (
    <div className="min-h-screen bg-ink" data-testid="admin-dashboard">
      <header className="border-b border-charcoal sticky top-0 bg-ink/90 backdrop-blur-xl z-50">
        <div className="mx-auto max-w-[1400px] px-6 sm:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <NorthStar className="w-4 h-4 text-warm" />
            <span className="font-sans text-sm tracking-[0.28em] uppercase text-warm">NSTR Talent</span>
            <span className="label !text-silver/50 ml-3 hidden sm:inline">Submissions</span>
          </div>
          <button onClick={logout} className="label link-underline !text-warm flex items-center gap-2" data-testid="admin-logout">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-6 sm:px-10 py-12">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-charcoal border border-charcoal mb-12">
          {[
            ["Applications", stats?.applications],
            ["New Apps", stats?.applications_new],
            ["Brand Inquiries", stats?.brand_inquiries],
            ["New Inquiries", stats?.brand_inquiries_new],
          ].map(([label, val]) => (
            <div key={label} className="bg-ink p-6 sm:p-8">
              <div className="font-serif text-5xl text-warm">{val ?? "—"}</div>
              <div className="label mt-3">{label}</div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-8 border-b border-charcoal mb-8">
          {[
            ["applications", `Applications (${apps.length})`],
            ["brands", `Brand Inquiries (${brands.length})`],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => { setTab(key); setExpanded(null); }}
              className={`label pb-4 -mb-px border-b transition-colors duration-300 ${
                tab === key ? "!text-warm border-warm" : "border-transparent hover:!text-warm"
              }`}
              data-testid={`tab-${key}`}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="label py-20 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="label py-20 text-center !text-silver/50">No submissions yet.</div>
        ) : (
          <div className="border-t border-charcoal">
            {items.map((it) => {
              const isOpen = expanded === it.id;
              const title = tab === "applications" ? it.full_name : (it.company || it.name);
              return (
                <div key={it.id} className="border-b border-charcoal" data-testid={`submission-${it.id}`}>
                  <button
                    onClick={() => setExpanded(isOpen ? null : it.id)}
                    className="w-full grid grid-cols-12 gap-4 items-center py-6 text-left group"
                  >
                    <div className="col-span-6 sm:col-span-5 flex items-center gap-4">
                      {it.status === "new" ? (
                        <span className="w-1.5 h-1.5 rounded-full bg-warm shrink-0" />
                      ) : (
                        <span className="w-1.5 h-1.5 rounded-full bg-charcoal shrink-0" />
                      )}
                      <span className="font-serif text-2xl text-warm truncate group-hover:translate-x-1 transition-transform duration-500">
                        {title}
                      </span>
                    </div>
                    <div className="hidden sm:block col-span-4 label !text-silver/60 truncate">
                      {tab === "applications" ? it.content_category || "—" : it.budget_range || "—"}
                    </div>
                    <div className="col-span-6 sm:col-span-3 label !text-silver/50 text-right">
                      {fmtDate(it.created_at)}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="pb-8 pt-2">
                      <table className="w-full">
                        <tbody>
                          {fields.map(([key, label]) => (
                            <tr key={key} className="align-top">
                              <td className="label !text-silver/50 py-2 pr-6 w-40 whitespace-nowrap">{label}</td>
                              <td className="py-2 text-warm font-light text-sm break-words">
                                {it[key] ? it[key] : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {it.status === "new" && (
                        <button
                          onClick={() => markReviewed(tab, it.id)}
                          className="mt-6 label link-underline !text-warm flex items-center gap-2"
                          data-testid={`mark-reviewed-${it.id}`}
                        >
                          <Check className="w-3.5 h-3.5" /> Mark as reviewed
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
